import { useState, useEffect, useCallback } from 'react';

/**
 * useSocialFeed - Hook for fetching and parsing RSS feed data from GovSky
 * 
 * @param {string} feedUrl - The RSS feed URL
 * @param {number} limit - Number of items to display
 * @param {string} searchQuery - Optional search query to filter items
 */
export const useSocialFeed = (
  feedUrl = 'https://govsky.app/api/rss/us/officials',
  limit = 20,
  searchQuery = ''
) => {
  const [data, setData] = useState([]);
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feedInfo, setFeedInfo] = useState(null);

  /**
   * Parse RSS XML and extract items
   */
  const parseRSSFeed = useCallback((xmlText) => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
    
    // Check for parsing errors
    const parseError = xmlDoc.querySelector('parsererror');
    if (parseError) {
      throw new Error('Failed to parse RSS feed');
    }

    // Extract channel info
    const channel = xmlDoc.querySelector('channel');
    const feedTitle = channel?.querySelector('title')?.textContent || 'Social Feed';
    const feedDescription = channel?.querySelector('description')?.textContent || '';
    const feedLink = channel?.querySelector('link')?.textContent || '';
    const lastBuildDate = channel?.querySelector('lastBuildDate')?.textContent || '';

    setFeedInfo({
      title: feedTitle,
      description: feedDescription,
      link: feedLink,
      lastBuildDate: lastBuildDate
    });

    // Extract items
    const items = xmlDoc.querySelectorAll('item');
    const parsedItems = Array.from(items).map((item, index) => {
      const title = item.querySelector('title')?.textContent || '';
      const link = item.querySelector('link')?.textContent || '';
      const guid = item.querySelector('guid')?.textContent || link;
      const pubDate = item.querySelector('pubDate')?.textContent || '';
      const description = item.querySelector('description')?.textContent || '';
      const creator = item.getElementsByTagName('dc:creator')[0]?.textContent || '';
      
      // Parse the content:encoded for additional data
      const contentEncoded = item.getElementsByTagName('content:encoded')[0]?.textContent || '';
      
      // Extract engagement stats from content (💬 0 🔁 2 ❤️ 7 format)
      const statsMatch = contentEncoded.match(/💬\s*(\d+)\s*🔁\s*(\d+)\s*❤️\s*(\d+)/);
      const engagement = statsMatch ? {
        comments: parseInt(statsMatch[1]) || 0,
        reposts: parseInt(statsMatch[2]) || 0,
        likes: parseInt(statsMatch[3]) || 0
      } : { comments: 0, reposts: 0, likes: 0 };

      // Extract images from content
      const imgMatch = contentEncoded.match(/<img[^>]+src="([^"]+)"/);
      const imageUrl = imgMatch ? imgMatch[1] : null;

      // Extract the handle from creator or description
      const handleMatch = description.match(/@([a-zA-Z0-9.-]+)/);
      const handle = handleMatch ? handleMatch[1] : '';

      // Clean up description - remove the creator prefix
      let cleanDescription = description;
      const creatorPrefix = `${creator} (@${handle}): `;
      if (cleanDescription.startsWith(creatorPrefix)) {
        cleanDescription = cleanDescription.substring(creatorPrefix.length);
      }

      return {
        id: guid || `item-${index}`,
        title: title,
        url: link,
        created_at: pubDate,
        description: cleanDescription,
        creator: creator,
        handle: handle,
        engagement: engagement,
        imageUrl: imageUrl,
        contentHtml: contentEncoded,
        // Map to FeedItem expected fields
        status_title: title,
        document_link: null
      };
    });

    return parsedItems;
  }, []);

  /**
   * Filter items based on search query
   */
  const filterItems = useCallback((items, query) => {
    if (!query.trim()) return items;
    
    const lowerQuery = query.toLowerCase();
    return items.filter(item => 
      item.title?.toLowerCase().includes(lowerQuery) ||
      item.description?.toLowerCase().includes(lowerQuery) ||
      item.creator?.toLowerCase().includes(lowerQuery) ||
      item.handle?.toLowerCase().includes(lowerQuery)
    );
  }, []);

  /**
   * Fetch the RSS feed
   */
  useEffect(() => {
    const fetchFeed = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await fetch(feedUrl);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const xmlText = await response.text();
        const items = parseRSSFeed(xmlText);
        
        setAllData(items);
        
        // Apply search filter and limit
        const filteredItems = filterItems(items, searchQuery);
        setData(filteredItems.slice(0, limit));
        
      } catch (err) {
        setError(err.message);
        console.error('useSocialFeed - Error fetching RSS:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFeed();
  }, [feedUrl, parseRSSFeed]);

  /**
   * Update displayed data when search or limit changes
   */
  useEffect(() => {
    if (allData.length > 0) {
      const filteredItems = filterItems(allData, searchQuery);
      setData(filteredItems.slice(0, limit));
    }
  }, [searchQuery, limit, allData, filterItems]);

  /**
   * Refresh the feed
   */
  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(feedUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const xmlText = await response.text();
      const items = parseRSSFeed(xmlText);
      
      setAllData(items);
      const filteredItems = filterItems(items, searchQuery);
      setData(filteredItems.slice(0, limit));
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [feedUrl, parseRSSFeed, filterItems, searchQuery, limit]);

  return { 
    data, 
    loading, 
    error, 
    feedInfo,
    totalCount: allData.length,
    displayedCount: data.length,
    refresh,
    hasMore: data.length < filterItems(allData, searchQuery).length
  };
};

export default useSocialFeed;